import {Auth} from 'aws-amplify'
import {setLocalStorageItemWithQuotaRetry} from '../../../../shared/utils/localStorageQuota'
import {getLogger} from '../../../../shared/utils/logUtils'
import {COGNITO_SESSION_EXPIRATION_ATTR} from '../../../../shared/utils/sessionExpirationPreference'
import {CustomCognitoUser, UserAttributes} from './_models'

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
const COGNITO_USER_STORAGE_KEY = 'cognito-user'

const getCognitoAuth = (): CustomCognitoUser | undefined => {
  if (!localStorage) {
    return
  }

  const lsCognitoUser = localStorage.getItem(COGNITO_USER_STORAGE_KEY)
  if (!lsCognitoUser) {
    return
  } else {
    return JSON.parse(lsCognitoUser) as CustomCognitoUser
  }
}

const setCognitoAuth = (auth: CustomCognitoUser) => {
  if (!localStorage) {
    return
  }
  const lsValue = JSON.stringify(auth)
  const ok = setLocalStorageItemWithQuotaRetry(COGNITO_USER_STORAGE_KEY, lsValue)
  if (!ok) {
    getLogger().error(
      'Could not persist cognito-user to localStorage (quota). Evicted viewer caches; session may be lost on reload.'
    )
  }
}

const removeCognitoAuth = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(COGNITO_USER_STORAGE_KEY)
  } catch (error) {
    console.error('COGNITO USER REMOVE ERROR', error)
  }
}

const getHostedUIAuth = (): boolean | undefined => {
  if (!localStorage) {
    return false
  }

  try {
    return localStorage.getItem('amplify-redirected-from-hosted-ui') === 'true'
  } catch (error) {
    console.error('HOSTED UI AUTH ERROR', error)
  }
}

const removeHostedUIAuth = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem('amplify-redirected-from-hosted-ui')
  } catch (error) {
    console.error('HOSTED UI AUTH ERROR', error)
  }
}

const updateUserAttributes = async (attributes: Partial<UserAttributes>): Promise<void> => {
  try {
    const cognitoAttributes: {[key: string]: string} = {}

    // Map UserAttributes to Cognito attribute names
    if (attributes.notifEmailJobStatus !== undefined) {
      cognitoAttributes['custom:notifEmailJobStatus'] = attributes.notifEmailJobStatus ? '1' : '0'
    }

    if (attributes.sessionExpirationHours !== undefined) {
      cognitoAttributes[COGNITO_SESSION_EXPIRATION_ATTR] = String(
        Math.floor(attributes.sessionExpirationHours)
      )
    }

    if (Object.keys(cognitoAttributes).length === 0) {
      throw new Error('No valid attributes to update')
    }

    await Auth.updateUserAttributes(await Auth.currentAuthenticatedUser(), cognitoAttributes)
  } catch (error) {
    throw new Error(`Failed to update user attributes: ${error}`)
  }
}

export {
  getCognitoAuth,
  setCognitoAuth,
  removeCognitoAuth,
  getHostedUIAuth,
  removeHostedUIAuth,
  updateUserAttributes,
  AUTH_LOCAL_STORAGE_KEY,
}
